import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import ScopeTable3458 from "./ScopeTable3458"; // Optional Scope dialog component

export default function Agilent3458ACalculator({ calibrator }) {
  const [functionType, setFunctionType] = useState("");
  const [subFunction, setSubFunction] = useState("");
  const [amplitude, setAmplitude] = useState("");
  const [ampUnit, setAmpUnit] = useState("");
  const [frequency, setFrequency] = useState("");
  const [scopeValue, setScopeValue] = useState("");
  const [calculatedUncertainty, setCalculatedUncertainty] = useState("");
  const [calculatedUncertaintyUnit, setCalculatedUncertaintyUnit] = useState("");
  const [uncertaintyInRatio, setUncertaintyInRatio] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);

  const buttonWidth = "120px";
  const validateNumeric = (value) => /^-?(\d+)?(\.\d*)?$/.test(value);

  const handleCopy = (value) => {
    if (!value) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).catch((err) => {
        console.error("Clipboard write failed:", err);
      });
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = value;
      textArea.style.position = "fixed";
      textArea.style.opacity = 0;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
      document.body.removeChild(textArea);
    }
  };

  const resetResults = () => {
    setScopeValue("");
    setCalculatedUncertainty("");
    setCalculatedUncertaintyUnit("");
    setUncertaintyInRatio("");
  };

  const handleCalculate = async () => {
    setErrorMessage("");
    resetResults();

    if (!validateNumeric(amplitude) || !validateNumeric(frequency)) {
      setErrorMessage("Please enter valid numeric values for amplitude and frequency!");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        function: functionType || "",
        subFunction: subFunction || "",
        amplitudeUnit: ampUnit || "",
        amplitude: amplitude || "",
        frequency: frequency || "",
      });

      // Example backend endpoint (adjust if needed)
      const response = await fetch(
        //`http://localhost:8080/api/calculator/calculate3458?${params.toString()}`
        `/api/calculator/calculate3458?${params.toString()}`
      );

      if (!response.ok) throw new Error("Server error: " + response.status);

      const data = await response.json();

      if (data.message) {
        setErrorMessage(data.message);
        return;
      }

      setScopeValue(data.scopeValue || "");
      setCalculatedUncertainty(data.calculatedUncertainty || "");
      setCalculatedUncertaintyUnit(ampUnit || "");
      setUncertaintyInRatio(data.uncertaintyInRatio || "");
    } catch (err) {
      setErrorMessage("Failed to fetch calculation: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Stack spacing={2}>
        {/* Title */}
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: "bold",
            color: "primary.dark",
            textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
            mb: 2,
          }}
        >
          {calibrator} Uncertainty Calculator
        </Typography>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {/* Function */}
        <TextField
          select
          label="Function"
          value={functionType}
          onChange={(e) => {
            setFunctionType(e.target.value);
            resetResults();
          }}
          fullWidth
        >
          <MenuItem value="">(None)</MenuItem>
          <MenuItem value="Voltage DC">Voltage DC</MenuItem>
          <MenuItem value="Voltage AC">Voltage AC</MenuItem>
          <MenuItem value="Current DC">Current DC</MenuItem>
          <MenuItem value="Current AC">Current AC</MenuItem>
          <MenuItem value="Resistance">Resistance</MenuItem>
          <MenuItem value="Frequency">Frequency</MenuItem>
        </TextField>

        {/* Sub Function */}
        <TextField
          select
          label="Sub Function"
          value={subFunction}
          onChange={(e) => {
            setSubFunction(e.target.value);
            resetResults();
          }}
          fullWidth
        >
          <MenuItem value="">(None)</MenuItem>
          {/* Optional: populate dynamically from DB if available */}
        </TextField>

        {/* Scope Button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ width: buttonWidth }}
            onClick={() => setScopeOpen(true)}
          >
            Show Scope
          </Button>
        </Box>

        {/* Amplitude & Unit */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Amplitude"
            value={amplitude}
            onChange={(e) => {
              setAmplitude(e.target.value);
              resetResults();
            }}
            fullWidth
          />
          <TextField
            select
            label="Unit"
            value={ampUnit}
            onChange={(e) => {
              setAmpUnit(e.target.value);
              resetResults();
            }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="V">V</MenuItem>
            <MenuItem value="kV">kV</MenuItem>
            <MenuItem value="mVrms">mVrms</MenuItem>
            <MenuItem value="Vrms">Vrms</MenuItem>
            <MenuItem value="nA">nA</MenuItem>
            <MenuItem value="µA">µA</MenuItem>
            <MenuItem value="mA">mA</MenuItem>
            <MenuItem value="A">A</MenuItem>
            <MenuItem value="Ohm">Ohm</MenuItem>
            <MenuItem value="kOhm">kOhm</MenuItem>
            <MenuItem value="MOhm">MOhm</MenuItem>
            <MenuItem value="GOhm">GOhm</MenuItem>
            <MenuItem value="Hz">Hz</MenuItem>
            <MenuItem value="kHz">kHz</MenuItem>
            <MenuItem value="MHz">MHz</MenuItem>
          </TextField>
        </Box>

        {/* Frequency */}
        <TextField
          label="Frequency (Hz)"
          value={frequency}
          onChange={(e) => {
            setFrequency(e.target.value);
            resetResults();
          }}
          fullWidth
        />

        {/* Calculate button */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            sx={{ width: buttonWidth }}
            onClick={() => {
              setCalculatedUncertaintyUnit(ampUnit);
              handleCalculate();
            }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Calculate"}
          </Button>
        </Box>

        {/* Results */}
        <Stack spacing={1}>
          <Typography>Allowed Scope Value: {scopeValue}</Typography>

          <Typography>
            Calculated Uncertainty: {calculatedUncertainty} {calculatedUncertaintyUnit}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => handleCopy(`${calculatedUncertainty}`)}
            >
              Copy
            </Button>
          </Box>

          <Typography>Uncertainty in Ratio: {uncertaintyInRatio}</Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => handleCopy(`${uncertaintyInRatio}`)}
            >
              Copy
            </Button>
          </Box>
        </Stack>
      </Stack>

      {/* Scope Table Dialog */}
      <ScopeTable3458
        open={scopeOpen}
        onClose={() => setScopeOpen(false)}
        functionType={functionType}
        subFunction={subFunction}
      />
    </Paper>
  );
}